/**
 * Ticket Z14: Öffentliche Einstiegsseite /100-tage (Druckadresse, QR-Code).
 *
 * Schlanke Seite: Einordnung, Hinweis auf die interaktive Fassung im
 * Kampagnencockpit, Weiterbildungsanfrage, Kontakt und Terminbuchung.
 * Texte sind im Backend pflegbar („Seite 100 Tage").
 */

import { createFileRoute, Link } from "@tanstack/react-router";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Mail, Phone } from "lucide-react";
import { Layout } from "@/components/Layout";
import { Reveal } from "@/components/Reveal";
import { BookingCtaButton } from "@/components/cta/BookingCtaButton";
import { getLandingContent } from "@/lib/landing-hundert-tage.functions";

export const Route = createFileRoute("/100-tage/")({
  loader: async () => ({ content: await getLandingContent() }),
  head: ({ loaderData }) => {
    const title = loaderData?.content.seoTitle ?? "100 Tage im Amt | creategy";
    const description =
      loaderData?.content.seoDescription ??
      "Der creategy-Fahrplan für die ersten 100 Tage im Amt.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: "https://creategy.de/100-tage" }],
    };
  },
  component: HundertTageLandingPage,
});

function HundertTageLandingPage() {
  const { content } = Route.useLoaderData();
  const telHref = `tel:${content.contactPhone.replace(/[^+\d]/g, "")}`;

  return (
    <Layout>
      {/* Sektion 1: Hero und Einordnung */}
      <section>
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <p className="font-display text-sm font-extrabold uppercase tracking-wide text-brand-wahlen">
            Amtszeit-Modul
          </p>
          <h1 className="heading-h1 mt-4 text-primary">{content.title}</h1>
          <div className="markdown-body mt-8 max-w-[68ch]">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{content.introMd}</ReactMarkdown>
          </div>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <BookingCtaButton
              variant="primary"
              eventPath="creategy/erstgespraech"
              label={content.ctaLabel}
              className="max-w-max"
            />
            <Link
              to="/100-tage/weiterbildung"
              className="inline-flex items-center justify-center rounded-sm border border-primary px-6 py-3 font-display text-sm font-extrabold text-primary transition-colors hover:bg-primary/5"
            >
              Kurse und Trainings anfragen
            </Link>
          </div>
        </div>
      </section>

      {/* Sektion 2: Hinweis auf das Kampagnencockpit */}
      {content.cockpitNoteMd && (
        <section aria-labelledby="hundert-tage-cockpit">
          <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
            <Reveal>
              <div className="rounded-lg border border-foreground/15 bg-muted/40 p-8 md:p-10">
                <h2
                  id="hundert-tage-cockpit"
                  className="heading-h2 text-primary"
                >
                  Die interaktive Fassung
                </h2>
                <div className="markdown-body mt-6 max-w-[68ch]">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {content.cockpitNoteMd}
                  </ReactMarkdown>
                </div>
                <Link
                  to="/cockpit"
                  className="mt-8 inline-flex items-center justify-center rounded-sm border border-primary px-6 py-3 font-display text-sm font-extrabold text-primary transition-colors hover:bg-primary/5"
                >
                  Zum Kampagnencockpit
                </Link>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* Sektion 3: Kontakt */}
      <section aria-labelledby="hundert-tage-kontakt">
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <Reveal>
            <h2 id="hundert-tage-kontakt" className="heading-h2 text-primary">
              Sprechen Sie uns an
            </h2>
            <p className="mt-6 max-w-[68ch] font-body text-base leading-relaxed text-foreground md:text-lg">
              Sie erreichen uns direkt, telefonisch oder per E-Mail. Wenn Sie lieber einen
              festen Termin möchten, buchen Sie ein kostenloses Erstgespräch.
            </p>
            <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-10">
              <a
                href={telHref}
                className="inline-flex items-center gap-3 font-body text-base text-foreground underline-offset-4 hover:underline"
              >
                <Phone className="h-5 w-5 shrink-0 text-primary" aria-hidden />
                {content.contactPhone}
              </a>
              <a
                href={`mailto:${content.contactEmail}`}
                className="inline-flex items-center gap-3 font-body text-base text-foreground underline-offset-4 hover:underline"
              >
                <Mail className="h-5 w-5 shrink-0 text-primary" aria-hidden />
                {content.contactEmail}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </Layout>
  );
}
